import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';

import {
  resolveSmartDate,
  SmartDateTone,
  SmartDateVariant,
} from '@/components/ui/smartDate/smartDate.util.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Props = {
  date?: string | null;
  emptyLabel?: string;
  isFinished?: boolean;
  variant?: SmartDateVariant;
  withTime?: boolean;
};

const toneSx: Record<SmartDateTone, object> = {
  default: { color: themeColors.greyDarker },
  muted: { color: themeColors.greyDark },
  overdue: { color: themeColors.red, fontWeight: 500 },
  today: { color: themeColors.orangeDark, fontWeight: 500 },
};

export const SmartDate: React.FC<Props> = ({
  date,
  emptyLabel,
  isFinished,
  variant = 'calendar',
  withTime,
}) => {
  const { t } = useTranslationWithPrefix('components.listDetails.date');
  const view = resolveSmartDate({ date, isFinished, t, variant, withTime });

  if (!view) {
    return (
      <Typography noWrap sx={{ color: themeColors.grey }} variant="f14Regular">
        {emptyLabel ?? '—'}
      </Typography>
    );
  }

  return (
    <Tooltip title={view.tooltip}>
      <Stack
        component="span"
        direction="row"
        spacing={0.5}
        sx={{ alignItems: 'center', display: 'inline-flex', whiteSpace: 'nowrap' }}
      >
        {view.tone === 'overdue' && (
          <ErrorOutlineIcon sx={{ color: themeColors.red, fontSize: 15 }} />
        )}
        <Typography component="span" sx={toneSx[view.tone]} variant="f14Regular">
          {view.label}
        </Typography>
      </Stack>
    </Tooltip>
  );
};
